import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import { PasswordStrengthMeter } from '../pages/ResetPassword';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIAL_RE = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]/;
const isStrong = (pw) =>
  pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw) && SPECIAL_RE.test(pw);

/**
 * ChangePasswordDialog — self-service password change for the logged-in user.
 * Requires current password + strong new password + confirm.
 * Opens from the top-right user dropdown menu.
 */
export default function ChangePasswordDialog({ open, onOpenChange, forced = false, onSuccess }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const ok = useMemo(() => current && isStrong(next) && next === confirm, [current, next, confirm]);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!ok) { toast.error('Fill all fields with a strong matching password'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current, new_password: next,
      });
      toast.success('Password updated successfully.');
      reset();
      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Change failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => {
      // Forced flow — admin reset with must_change_password=true. Prevent
      // dismissing so the user cannot bypass the mandatory change.
      if (forced && !o) return;
      if (!o) reset(); onOpenChange(o);
    }}>
      <DialogContent className="max-w-md"
        data-testid="change-password-dialog"
        onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}
        onInteractOutside={(e) => { if (forced) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {forced ? 'Password Change Required' : 'Change Password'}
          </DialogTitle>
          <DialogDescription>
            {forced
              ? 'An administrator issued you a temporary password. Please set a new password to continue.'
              : "Choose a strong password. You'll stay logged in on this device."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <div>
            <Label htmlFor="cur">Current password</Label>
            <div className="relative">
              <Input id="cur" type={show ? 'text' : 'password'} value={current}
                onChange={(e) => setCurrent(e.target.value)} required data-testid="cp-current" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="new">New password</Label>
            <Input id="new" type={show ? 'text' : 'password'} value={next}
              onChange={(e) => setNext(e.target.value)} required data-testid="cp-new" />
            <PasswordStrengthMeter password={next} />
          </div>
          <div>
            <Label htmlFor="conf">Confirm new password</Label>
            <Input id="conf" type={show ? 'text' : 'password'} value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required data-testid="cp-confirm" />
            {confirm && next !== confirm && (
              <p className="text-[11px] text-rose-600 mt-1">Passwords don&apos;t match</p>
            )}
          </div>
          <DialogFooter className="pt-2">
            {!forced && (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            )}
            <Button type="submit" disabled={busy || !ok} data-testid="cp-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {forced ? 'Set New Password' : 'Update Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
